import React, { useCallback, useEffect, useState } from 'react';
import { reportApi } from '../services/api';
import Brand from './Brand';
import HeaderNav from './HeaderNav';
import ResearchDisclaimer from './ResearchDisclaimer';
import EarningsCalendar from './EarningsCalendar';
import IndependentDirectorBuys from './IndependentDirectorBuys';
import InsiderTrading from './InsiderTrading';
import MarketPulse from './MarketPulse';
import MarketCap from './MarketCap';
import LatestNews from './LatestNews';
import PharmaLandscape from './PharmaLandscape';
import SearchDiscover from './SearchDiscover';

const HUB_LABELS = {
  pulse: 'Market Pulse',
  discover: 'Search & Discover',
  news: 'Latest News',
  calendar: 'Earnings Calendar',
  insider: 'Insider Trading',
  insider_board: 'Independent Director Buys',
  pharma: 'Pharma Landscape',
  marketcap: 'Market Cap',
  contest: 'Contest'
};

const list = (value) => (Array.isArray(value) ? value : []);
const titleOf = (hub, fallback) => fallback || HUB_LABELS[hub] || hub;
const tickerOf = (row) => String(row?.ticker || '').toUpperCase();
const textOf = (value) => (value == null || value === '' ? '—' : String(value));
const labelize = (value) => String(value || '').replaceAll('_', ' ');
const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};
const flattenRows = (doc) => {
  const sectionRows = list(doc?.sections).flatMap((section) => list(section.rows).map((row) => ({
    ...row,
    _section: section.title || section.group || section.id,
    _group: row.group || section.group || section.title
  })));
  if (sectionRows.length) return sectionRows;
  const data = doc?.data;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.items)) return data.items;
  if (data && typeof data === 'object') {
    return Object.entries(data).flatMap(([group, value]) => {
      const rows = Array.isArray(value) ? value : Array.isArray(value?.rows) ? value.rows : [];
      if (rows.length) return rows.map((row) => ({ ...row, _group: group }));
      return value && typeof value === 'object' ? [{ ticker: value.ticker || group, ...value, _group: group }] : [];
    });
  }
  return [];
};

const fieldValue = (value) => {
  if (value == null || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return Object.entries(value).map(([key, item]) => `${labelize(key)}: ${fieldValue(item)}`).join(' · ');
  return String(value);
};

const CompanyLink = ({ row, children, onOpenCompany }) => {
  const ticker = tickerOf(row);
  if (!ticker) return <>{children || textOf(row?.company || row?.name)}</>;
  const open = (event) => {
    if (!onOpenCompany || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    onOpenCompany(ticker);
  };
  return <a href={`/company/${encodeURIComponent(ticker)}`} onClick={open}>{children || row.company || row.name || ticker}</a>;
};

const GenericTable = ({ rows, onOpenCompany }) => {
  const visibleRows = list(rows);
  if (!visibleRows.length) return <div className="empty-state">No rows to display.</div>;
  const keys = Array.from(new Set(visibleRows.flatMap((row) => Object.keys(row).filter((key) => !key.startsWith('_'))))).slice(0, 10);
  return (
    <div className="admin-table-scroll">
      <table className="admin-table hub-table">
        <thead><tr>{keys.map((key) => <th key={key}>{labelize(key)}</th>)}</tr></thead>
        <tbody>{visibleRows.map((row, index) => (
          <tr key={`${tickerOf(row) || index}-${index}`}>{keys.map((key) => (
            <td key={key}>{key === 'ticker' && row[key] ? <CompanyLink row={row} onOpenCompany={onOpenCompany}>{row[key]}</CompanyLink> : fieldValue(row[key])}</td>
          ))}</tr>
        ))}</tbody>
      </table>
    </div>
  );
};

const SectionsAsTables = ({ doc, onOpenCompany }) => {
  const sections = list(doc.sections);
  if (!sections.length) return <GenericTable rows={flattenRows(doc)} onOpenCompany={onOpenCompany} />;
  return <div className="hub-list">{sections.map((section) => <section className="panel" key={section.id || section.title || section.group}><p className="eyebrow">{section.group || section.id || 'Section'}</p><h2>{section.title || section.group || 'Section'}</h2><GenericTable rows={section.rows || []} onOpenCompany={onOpenCompany} /></section>)}</div>;
};

const renderHub = (hub, doc, access, onOpenCompany, canAdminRefresh, onRefresh, publicMode, isEmployee, discoverySearchTarget) => {
  if (hub === 'pulse') return <MarketPulse doc={{ ...doc, access }} onOpenCompany={onOpenCompany} canAdminRefresh={canAdminRefresh} onRefresh={onRefresh} publicMode={publicMode} hideIncludedLabel={isEmployee} />;
  if (hub === 'marketcap') return <MarketCap doc={{ ...doc, access }} onOpenCompany={onOpenCompany} publicMode={publicMode} />;
  if (hub === 'pharma') return <PharmaLandscape doc={{ ...doc, access }} onOpenCompany={onOpenCompany} publicMode={publicMode} />;
  if (hub === 'discover') return <SearchDiscover doc={{ ...doc, access }} onOpenCompany={onOpenCompany} publicMode={publicMode} isEmployee={isEmployee} searchTarget={discoverySearchTarget} />;
  if (hub === 'news') return <LatestNews doc={{ ...doc, access }} onOpenCompany={onOpenCompany} publicMode={publicMode} showCoverage={canAdminRefresh} />;
  if (hub === 'calendar') return <EarningsCalendar doc={doc} rows={flattenRows(doc)} onOpenCompany={onOpenCompany} />;
  if (hub === 'insider') return <InsiderTrading doc={doc} onOpenCompany={onOpenCompany} />;
  if (hub === 'insider_board') return <IndependentDirectorBuys doc={doc} onOpenCompany={onOpenCompany} />;
  return <SectionsAsTables doc={doc} onOpenCompany={onOpenCompany} />;
};

const HubPage = ({ hubId, user, publicMode = false, onBack, onLogout, onOpenProfile, onOpenCompany }) => {
  const [doc, setDoc] = useState(null);
  const [versions, setVersions] = useState([]);
  const [version, setVersion] = useState(new URLSearchParams(window.location.search).get('version') || '');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [discoverySearchTarget, setDiscoverySearchTarget] = useState(null);
  const isAdmin = user?.permissions?.includes('admin') || user?.email?.toLowerCase() === 'greg@hffinancial.com';

  const loadHub = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const search = new URLSearchParams(window.location.search);
      const requestParams = { version: version || undefined };
      if (hubId !== 'news') Object.assign(requestParams, { group: search.get('group'), ticker: search.get('ticker') });
      const result = publicMode ? await reportApi.publicHub(hubId, requestParams) : await reportApi.hub(hubId, requestParams);
      setDoc(result);
    } catch (err) {
      setError(err.status === 403 ? 'This hub is locked for your current plan.' : ['calendar', 'insider_board', 'insider', 'marketcap', 'news', 'pharma'].includes(hubId) && err.status === 404 && err.body?.detail === 'Hub not built' ? `${hubId === 'calendar' ? 'The earnings calendar' : HUB_LABELS[hubId]} has not been published yet.` : err.message);
      setDoc(null);
    } finally {
      setLoading(false);
    }
  }, [hubId, publicMode, version]);

  const refreshLatest = useCallback(() => {
    if (version) setVersion('');
    else loadHub();
  }, [version, loadHub]);

  useEffect(() => {
    loadHub();
  }, [loadHub]);

  useEffect(() => {
    if (publicMode) return undefined;
    reportApi.hubVersions(hubId).then((result) => setVersions(list(result.versions))).catch(() => setVersions([]));
    return undefined;
  }, [hubId, publicMode]);

  const page = doc?.page || doc || {};
  const access = page.access || doc?.access || {};
  const publicationNote = hubId === 'calendar' ? 'Earnings dates require a successful calendar refresh and publication.'
    : hubId === 'pharma' ? 'Pharma Landscape requires shared lead-asset data, disease taxonomy and publication.'
    : hubId === 'news' ? 'Latest News requires a shared news refresh and publication.'
    : hubId === 'insider' ? 'Insider Trading requires shared SEC trade data and publication.'
      : hubId === 'pulse' ? 'Market Pulse requires shared quotes and publication.'
        : hubId === 'marketcap' ? 'Market Cap requires shared market-cap data and publication.'
          : 'Director buys require shared SEC trade data, identity review and publication.';
  const scopeNote = hubId === 'discover' && doc ? 'Showing the full roster. First Look and tracking access depend on your plan.' : access.news_audience ? {
    all: 'Showing all companies for employees.',
    free: "Showing your plan's free company.",
    tracked: 'Showing only your tracked companies.',
    tracked_and_shared: 'Showing your tracked and included companies.',
  }[access.news_audience] : access.scope === 'tracked_included' ? 'Showing your tracked and included companies.' : access.scope === 'free' ? "Showing your plan's free company." : access.tracked_only ? 'Showing only your tracked companies within your plan access.' : access.scope ? `Showing ${access.scope === 'all' ? 'full roster' : access.scope === 'mine' ? 'your names' : access.scope === 'halo' ? 'your groups' : access.scope} access${access.hidden_rows ? ` · ${access.hidden_rows} hidden rows` : ''}.` : '';

  return (
    <div className="app-shell">
      <header className="topbar">
        <Brand href={publicMode ? '/plans' : '/'} onClick={(event) => { if (onBack && !event.metaKey && !event.ctrlKey && !event.shiftKey && event.button === 0) { event.preventDefault(); onBack(); } }} />
        <HeaderNav user={user} publicMode={publicMode} onLogout={onLogout} onOpenProfile={onOpenProfile} />
      </header>
      <main className="workspace hub-workspace">
        <section className={`workspace-intro hub-intro${hubId === 'discover' ? ' discovery-intro' : ''}`}>
          <div><p className="eyebrow">Cross-company hub</p><h1>{titleOf(hubId, page.title)}.</h1></div>
          {hubId === 'discover' ? <div className="discovery-header-search">
            <p className="intro-copy">Explore the research roster.</p>
            <div ref={setDiscoverySearchTarget} />
          </div> : <p className="intro-copy">{hubId === 'pharma' ? 'Snapshot published' : 'Fresh as of'} {formatDateTime(hubId === 'pharma' ? page.generated_at : ['insider', 'pulse', 'marketcap', 'news'].includes(hubId) ? page.as_of : page.as_of || page.generated_at)}.</p>}
        </section>
        <div className="hub-controls">
          {!publicMode && versions.length > 1 && <label>Version<select value={version} onChange={(event) => setVersion(event.target.value)}><option value="">Latest</option>{versions.map((item) => <option key={item.version_id} value={item.version_id}>{item.created_at ? `${item.version_id} · ${formatDateTime(item.created_at)}` : item.version_id}</option>)}</select></label>}
          {scopeNote && <span className="muted">{scopeNote} {hubId !== 'discover' && !access.news_audience && <a href="/plans">Upgrade access</a>}</span>}
        </div>
        {loading ? <div className="empty-state">Loading hub...</div> : error ? <div className="panel hub-locked" role="alert"><h2>{error}</h2>{['calendar', 'insider_board', 'insider', 'pulse', 'marketcap', 'news', 'pharma'].includes(hubId) && error !== 'This hub is locked for your current plan.' ? <><p className="muted">{publicationNote} Retry to check availability.</p><button className="primary-button" onClick={loadHub}>Retry</button>{hubId === 'pulse' && isAdmin && !publicMode && <MarketPulse doc={{ sections: [] }} canAdminRefresh onRefresh={refreshLatest} />}</> : <><p className="muted">Compare plans to unlock more cross-company intelligence.</p><a className="primary-button" href="/plans">View plans</a></>}</div> : renderHub(hubId, page, access, onOpenCompany, isAdmin && !publicMode, refreshLatest, publicMode, user?.plan === 'employee', discoverySearchTarget)}
        <ResearchDisclaimer />
      </main>
    </div>
  );
};

export default HubPage;
